import { useEffect, useState } from 'react';
import { superAdminApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormatter';

interface AuditLog {
    id: string;
    actorEmail?: string;
    action: string;
    targetType?: string;
    targetId?: string;
    details?: Record<string, any>;
    createdAt: string;
}

export default function AuditLogsPage() {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [actionFilter, setActionFilter] = useState('');
    const limit = 20;

    useEffect(() => {
        loadLogs();
    }, [page, actionFilter]);

    async function loadLogs() {
        setLoading(true);
        try {
            const data = await superAdminApi.listAuditLogs({
                limit,
                offset: page * limit,
                action: actionFilter || undefined,
            });
            setLogs(data.logs || []);
            setTotal(data.total || 0);
        } catch (error) {
            console.error('Failed to load audit logs:', error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            <h1 className="text-2xl font-bold mb-6">Audit Logs</h1>

            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-4 mb-6 flex gap-4 flex-wrap">
                <select
                    value={actionFilter}
                    onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                >
                    <option value="">All Actions</option>
                    <option value="tenant.updated">Tenant Updated</option>
                    <option value="plan.created">Plan Created</option>
                    <option value="plan.updated">Plan Updated</option>
                    <option value="subscription.updated">Subscription Updated</option>
                </select>
                <span className="text-gray-400 self-center">
                    Showing {logs.length} of {total} entries
                </span>
            </div>

            {/* Logs Table */}
            <div className="bg-gray-800 rounded-lg overflow-hidden">
                {loading ? (
                    <div className="text-center py-8">Loading...</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-700">
                            <tr className="text-left text-gray-300 text-sm">
                                <th className="px-4 py-3">Time</th>
                                <th className="px-4 py-3">Actor</th>
                                <th className="px-4 py-3">Action</th>
                                <th className="px-4 py-3">Target</th>
                                <th className="px-4 py-3">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {logs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-700/50">
                                    <td className="px-4 py-3 text-sm text-gray-400">
                                        {formatDateTime(log.createdAt)}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {log.actorEmail || '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <ActionBadge action={log.action} />
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {log.targetType && (
                                            <span className="text-gray-400">
                                                {log.targetType}
                                                {log.targetId && <span className="text-xs ml-1">({log.targetId.slice(0, 8)}...)</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {log.details && (
                                            <button
                                                onClick={() => alert(JSON.stringify(log.details, null, 2))}
                                                className="text-purple-400 hover:text-purple-300"
                                            >
                                                View
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {logs.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-400">
                        No audit logs found
                    </div>
                )}
            </div>

            {/* Pagination */}
            {total > limit && (
                <div className="flex justify-center gap-2 mt-4">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="px-4 py-2 bg-gray-700 rounded disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="px-4 py-2 text-gray-400">
                        Page {page + 1} of {Math.ceil(total / limit)}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= total}
                        className="px-4 py-2 bg-gray-700 rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

function ActionBadge({ action }: { action: string }) {
    const colors: Record<string, string> = {
        'tenant.created': 'bg-green-600',
        'tenant.updated': 'bg-blue-600',
        'plan.created': 'bg-purple-600',
        'plan.updated': 'bg-purple-600',
        'subscription.updated': 'bg-yellow-600',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${colors[action] || 'bg-gray-600'}`}>
            {action}
        </span>
    );
}
